import type { LucideIcon } from "lucide-react";
import {
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

export type ResultDialogMessage = {
  title: string;
  body: string;
  confirmBtnLabel: string;
  icon?: LucideIcon;
  iconClassName?: string;
  /** Optional secondary action (e.g. "Try Again"), rendered before the confirm button. */
  secondaryBtnLabel?: string;
  onSecondaryClick?: () => void;
};

type ResultDialogProps = {
  message: ResultDialogMessage;
  onClose: () => void;
  testId?: string;
  secondaryTestId?: string;
};

const AlertMessageDialog = ({
  message,
  onClose,
  testId,
  secondaryTestId,
}: ResultDialogProps) => {
  return (
    <AlertDialogContent className="flex min-h-[200px] max-w-[350px] flex-col items-center justify-between gap-2 rounded-lg sm:max-w-lg">
      {message.icon && (
        <div
          className={`flex w-fit justify-center rounded-full p-3 ${message.iconClassName ?? ""}`}
        >
          <message.icon className="size-10" strokeWidth={1.2} />
        </div>
      )}
      <AlertDialogHeader className="items-center">
        <AlertDialogTitle className="text-lg font-medium">
          {message.title}
        </AlertDialogTitle>
      </AlertDialogHeader>
      <AlertDialogDescription className="flex w-full items-center justify-center text-center">
        {message.body}
      </AlertDialogDescription>
      <AlertDialogFooter className="mt-2 flex w-full flex-row items-center gap-2 rounded-lg px-2 sm:justify-center">
        <div className="flex w-full items-center justify-center gap-3">
          {message.secondaryBtnLabel && message.onSecondaryClick && (
            <Button
              variant="outline"
              type="button"
              onClick={message.onSecondaryClick}
              className="min-w-28 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              data-testid={secondaryTestId}
            >
              {message.secondaryBtnLabel}
            </Button>
          )}
          <Button
            variant="default"
            type="button"
            onClick={onClose}
            className="min-w-28 outline-none hover:bg-primary-dark focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            data-testid={testId}
          >
            {message.confirmBtnLabel}
          </Button>
        </div>
      </AlertDialogFooter>
    </AlertDialogContent>
  );
};

export default AlertMessageDialog;
